import React, { Dispatch, FunctionComponent, useMemo } from 'react'
import sortBy from 'lodash-es/sortBy'
import classNames from 'classnames'
import AdditionalInfoField from './AdditionalInfoField'
import styles from './AdditionalInfoBlock.module.css'
import actionContainerStyles from '@/styles/templates/ActionContainer.module.css'
import TXT from '@/messages'
import { useOurStore } from '@/app/_providers/StoreProvider'
import FadeInContainer from '@/app/_components/FadeInContainer/FadeInContainer'
import TicketCategory = Transformed.TicketCategory
import TicketTier = Transformed.TicketTier
import AdditionalInfoResponse = DataAccessInput.AdditionalInfoResponse

type Props = {
  categories: readonly TicketCategory[]
  isEvents?: boolean
  tiers: readonly TicketTier[]
  visibleStatus: Record<string, boolean>
  setValues: Dispatch<AdditionalInfoResponse[]>
}

const AdditionalInfoBlock: FunctionComponent<Props> = ({
  categories,
  tiers,
  visibleStatus,
  setValues,
  isEvents = false,
}: Props) => {
  const { additionalInfoResponses } = useOurStore((state) => state)
  // TODO: is this needed?
  const categoriesWithTiers = useMemo(() => categories.filter((category) => {
    const matchedTiers = tiers.filter((tier) => tier.categoryId === category.id)
    return matchedTiers.length > 0
  }), [categories, tiers])

  /**
   * only return categories with
   * - additional info
   * - tiers
   * - added tickets (visibleStatus)
   */
  const categoriesWithAdditionalInfo = useMemo(() => categories.filter((category) => {
    const matchedTiers = tiers.filter((tier) => tier.categoryId === category.id)
    return matchedTiers.length > 0 && category.additionalInfo
  }), [categories, tiers])

  const nextCategories = useMemo(() => {
    if (categoriesWithAdditionalInfo.length > 1) {
      return categoriesWithAdditionalInfo.filter((category) => visibleStatus[category.id])
    }
    return categoriesWithAdditionalInfo
  }, [categoriesWithAdditionalInfo, visibleStatus])

  // update visible status state
  const handleSetValues = (next: AdditionalInfoResponse): void => {
    setValues([next])
  }

  const isVisible = useMemo(() => (
    Object.values(visibleStatus).includes(true)
  ), [categoriesWithTiers, visibleStatus])

  // less than one category, or something selected
  return (
    <FadeInContainer
      testId="additional-info-container"
      isVisible={isVisible}
      className={classNames(
        actionContainerStyles.Section,
        actionContainerStyles.Alt,
        { [actionContainerStyles.Events]: isEvents },
      )}
    >
      <h2 className={actionContainerStyles.SectionTitle}>{TXT.titles.steps.additionalInfo}</h2>
      {nextCategories.map((category) => {
        const { id, additionalInfo: additionalInfoList } = category
        const filteredResponses = additionalInfoResponses
          .filter(({ categoryId }) => categoryId === id)

        return (
          <div className={styles.CategoryGroup} key={id}>
            {categoriesWithAdditionalInfo.length > 1 && (
              <div className={actionContainerStyles.SectionTitle}>{category.title}</div>
            )}
            {sortBy(additionalInfoList, 'order').map((additionalInfo) => (
              <div
                className="form-group"
                key={additionalInfo.id}
              >
                <AdditionalInfoField
                  handleUpdateValues={(args) => {
                    handleSetValues({
                      ...args,
                      categoryId: id,
                    })
                  }}
                  additionalInfo={additionalInfo}
                  incomingValue={
                    // add the existing response
                    filteredResponses?.find(
                      (response) => response.id === additionalInfo.id,
                    )?.value
                  }
                />
              </div>
            ))}
          </div>
        )
      })}
    </FadeInContainer>
  )
}

export default AdditionalInfoBlock
